/**
 * Comptabilité du cabinet — helpers partagés (Batch #14.265).
 */
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Loader2, X } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { fmtCAD, today } from "@/lib/format";

/** Batch #14.271 — préfixe API de la portée : « /gl » (cabinet) ou « /books/{master_id} » (tenue de livres d'un client). */
const GlBaseContext = createContext("/gl");
export const GlBaseProvider = GlBaseContext.Provider;
export const useGlBase = () => useContext(GlBaseContext);

export const money = fmtCAD;
export { today };
export const errMsg = (e, fallback = "Erreur") => e?.response?.data?.detail || fallback;

export const inputCls = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-300";
export const TYPE_LABEL = { asset: "Actif", liability: "Passif", equity: "Capitaux propres", revenue: "Revenus", expense: "Dépenses" };
export const TYPE_ORDER = ["asset", "liability", "equity", "revenue", "expense"];
export const KIND_LABEL = { H: "En-tête", A: "Compte", T: "Total" };
export const JOURNAL_LABEL = { GEN: "Général", ACH: "Achats", DEC: "Décaissements", VEN: "Ventes", ENC: "Encaissements" };
export const JOURNAL_CLS = {
  GEN: "bg-slate-100 text-slate-700 border-slate-200", ACH: "bg-amber-100 text-amber-800 border-amber-200", DEC: "bg-rose-100 text-rose-800 border-rose-200",
  VEN: "bg-emerald-100 text-emerald-800 border-emerald-200", ENC: "bg-sky-100 text-sky-800 border-sky-200",
};
export const SOURCE_LABEL = {
  manual: "Manuelle", opening: "Soldes d'ouverture", transfer: "Virement entre comptes", bank: "Relevé bancaire", expense: "Facture fournisseur", expense_payment: "Paiement fournisseur", invoice: "Facture client", invoice_payment: "Encaissement client",
  sale: "Vente", purchase: "Achat", sale_credit: "Note de crédit client", purchase_credit: "Note de crédit fournisseur", receipt: "Encaissement", disbursement: "Décaissement",
  depreciation: "Amortissement (registre)", asset_disposal: "Cession d'immobilisation", closing: "Fermeture de l'exercice", qbo: "QuickBooks (synchro)",
};
export const MONTHS_LONG = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
export const PAYMENT_METHODS = ["Virement", "Chèque", "Carte de crédit", "Carte de débit", "Comptant", "Prélèvement", "Stripe", "Autre"];

export function Pill({ children, cls = "bg-slate-100 text-slate-700 border-slate-200", testId, title }) {
  return <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${cls}`} data-testid={testId} title={title}>{children}</span>;
}

export function Btn({ children, onClick, tone = "emerald", busy, disabled, icon: Icon, testId, className = "", title, type = "button" }) {
  const tones = {
    emerald: "bg-emerald-600 hover:bg-emerald-700 text-white",
    sky: "bg-sky-600 hover:bg-sky-700 text-white",
    rose: "bg-rose-600 hover:bg-rose-700 text-white",
    amber: "bg-amber-600 hover:bg-amber-700 text-white",
    slate: "bg-white border border-slate-300 text-slate-700 hover:bg-slate-50",
    ghost: "text-slate-600 hover:bg-slate-100",
    danger: "bg-white border border-rose-200 text-rose-600 hover:bg-rose-50",
  };
  return (
    <button type={type} onClick={onClick} disabled={busy || disabled} title={title}
      className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 sm:py-1.5 rounded-lg text-xs font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed ${tones[tone]} ${className}`}
      data-testid={testId}>
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : Icon && <Icon className="w-3.5 h-3.5" />}
      {children}
    </button>
  );
}

export function Field({ label, children, className = "", hint, error, errorTestId }) {
  return (
    <div className={className}>
      <label className="block text-[11px] font-semibold text-slate-600 mb-1">{label}</label>
      {children}
      {hint && !error && <p className="text-[10px] text-slate-400 mt-0.5">{hint}</p>}
      {error && <p className="text-[10px] text-rose-500 mt-0.5" data-testid={errorTestId}>{error}</p>}
    </div>
  );
}

export function Modal({ title, sub, onClose, children, footer, wide = false, xwide = false, testId }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-0 sm:p-4 overflow-y-auto" onClick={onClose} data-testid={testId}>
      <div className={`bg-white sm:rounded-2xl shadow-xl w-full min-h-full sm:min-h-0 ${xwide ? "max-w-7xl" : wide ? "max-w-5xl" : "max-w-2xl"} sm:my-6 flex flex-col`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-4 sm:p-5 border-b sticky top-0 bg-white z-10 sm:static">
          <div>
            <h3 className="font-bold text-slate-900">{title}</h3>
            {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1" data-testid={testId ? `${testId}-close` : undefined}><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 sm:p-5 space-y-4 flex-1">{children}</div>
        {footer && <div className="flex flex-wrap justify-end gap-2 p-3 sm:p-4 border-t bg-slate-50 sm:rounded-b-2xl sticky bottom-0 sm:static pb-safe">{footer}</div>}
      </div>
    </div>
  );
}

export function Empty({ icon: Icon, text, children, testId }) {
  return (
    <div className="px-4 py-12 text-center text-slate-400" data-testid={testId}>
      {Icon && <Icon className="w-8 h-8 mx-auto mb-2 opacity-40" />}
      <p className="text-sm">{text}</p>
      {children && <div className="mt-3 flex justify-center gap-2">{children}</div>}
    </div>
  );
}

export async function downloadPdf(path, filename, setBusy) {
  setBusy?.(true);
  try {
    const r = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    toast.success(`${filename} téléchargé`);
  } catch (e) {
    let msg = "Erreur de téléchargement";
    try { msg = JSON.parse(await e.response?.data?.text())?.detail || msg; } catch { /* blob non JSON */ }
    toast.error(msg);
  } finally { setBusy?.(false); }
}

/** Même chose en POST (corps JSON → PDF). */
export async function downloadPostPdf(path, body, filename, setBusy) {
  setBusy?.(true);
  try {
    const r = await api.post(path, body, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    toast.success(`${filename} téléchargé`);
  } catch (e) {
    let msg = "Erreur de téléchargement";
    try { msg = JSON.parse(await e.response?.data?.text())?.detail || msg; } catch { /* blob non JSON */ }
    toast.error(msg);
  } finally { setBusy?.(false); }
}

/** Plan comptable (sans soldes) — partagé par les sélecteurs. */
export function useAccounts() {
  const base = useGlBase();
  const [accounts, setAccounts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const reload = useCallback(async () => {
    try { const r = await api.get(`${base}/accounts`, { params: { with_balances: false } }); setAccounts(r.data.items || []); }
    catch { setAccounts([]); }
    finally { setLoaded(true); }
  }, [base]);
  useEffect(() => { reload(); }, [reload]);
  return { accounts, reload, loaded };
}

/** Sélecteur de compte de détail (actif), groupé par type. `types` restreint aux types donnés (et fixe l'ordre des groupes : le type principal en premier). */
export function AccountSelect({ accounts, value, onChange, types, placeholder = "— Choisir un compte —", testId, className = inputCls, disabled }) {
  const list = (accounts || []).filter((a) => a.kind === "A" && (a.is_active !== false || a.id === value) && (!types || types.includes(a.type)));
  const groups = (types || TYPE_ORDER).filter((t) => list.some((a) => a.type === t));
  return (
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={className} data-testid={testId} disabled={disabled}>
      <option value="">{placeholder}</option>
      {groups.map((t) => (
        <optgroup key={t} label={TYPE_LABEL[t]}>
          {list.filter((a) => a.type === t).map((a) => <option key={a.id} value={a.id}>{`${a.number} — ${a.name}${a.is_active === false ? " (inactif)" : ""}`}</option>)}
        </optgroup>
      ))}
    </select>
  );
}

export const accLabel = (accounts, id) => { const a = (accounts || []).find((x) => x.id === id); return a ? `${a.number} — ${a.name}` : "—"; };
